import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Sparkles, Facebook } from 'lucide-react';
import { contactInfo as defaultContactInfo } from '../data';
import { useLanguage } from '../context/LanguageContext';
import { useCmsContent } from '../context/CmsImagesContext';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const { getImage, getText, companyInfo } = useCmsContent();
  const contactInfo = companyInfo || defaultContactInfo;

  return (
    <footer className="bg-gray-50 border-t border-gray-200 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <img 
                src={getImage('logo', '/images/ftr_logo_v1.png', { width: 250 })} 
                alt="FTR Service Logo" 
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              {getText('footer_tekst', 'tekst', t({ 
                da: "Professionel rengøring til hjem og erhverv i Billund, Varde og Vejle kommune. Vi vægter kvalitet, tryghed og miljø højt.",
                en: "Professional cleaning for home and business in Billund, Varde and Vejle municipality. We value quality, safety and the environment highly."
              }))}
            </p>
            <div className="flex space-x-4">
              <a 
                href={contactInfo.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#1877F2] transition-colors"
                aria-label={t({ da: "Følg os på Facebook", en: "Follow us on Facebook" })}
              >
                <Facebook className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">
              {t({ da: "Hurtige links", en: "Quick links" })}
            </h3>
            <ul className="space-y-4">
              <li><Link to="/tjenester" className="text-gray-600 hover:text-[#85d7f9] text-sm">{t({ da: "Tjenester", en: "Services" })}</Link></li>
              <li><Link to="/erhvervsrengoring" className="text-gray-600 hover:text-[#85d7f9] text-sm">{t({ da: "Erhverv", en: "Business" })}</Link></li>
              <li><Link to="/privatrengoring" className="text-gray-600 hover:text-[#85d7f9] text-sm">{t({ da: "Privat", en: "Private" })}</Link></li>
              <li><Link to="/galleri" className="text-gray-600 hover:text-[#85d7f9] text-sm">{t({ da: "Galleri", en: "Gallery" })}</Link></li>
              <li><Link to="/om-os" className="text-gray-600 hover:text-[#85d7f9] text-sm">{t({ da: "Om os", en: "About us" })}</Link></li>
              <li><Link to="/nyheder" className="text-gray-600 hover:text-[#85d7f9] text-sm">{t({ da: "Nyheder", en: "News" })}</Link></li>
              <li><Link to="/kontakt" className="text-gray-600 hover:text-[#85d7f9] text-sm">{t({ da: "Kontakt", en: "Contact" })}</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">
              {t({ da: "Vores Fokus", en: "Our Focus" })}
            </h3>
            <ul className="space-y-4 text-sm text-gray-600">
              <li>{t({ da: "Kontorrengøring", en: "Office cleaning" })}</li>
              <li>{t({ da: "Privat rengøring", en: "Private cleaning" })}</li>
              <li>{t({ da: "Flytterengøring", en: "Move-out cleaning" })}</li>
              <li>{t({ da: "Køkken & Bad", en: "Kitchen & Bath" })}</li>
              <li>{t({ da: "Hovedrengøring", en: "Deep cleaning" })}</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6">
              {t({ da: "Kontakt", en: "Contact" })}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-brand-primary shrink-0" />
                <span className="text-sm text-gray-600">
                  {contactInfo.owner}<br />
                  {contactInfo.address}<br />
                  {contactInfo.zipCity}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-brand-primary shrink-0" />
                <a href={`tel:${contactInfo.phone}`} className="text-sm text-gray-600 hover:text-[#85d7f9] transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-brand-primary shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-sm text-gray-600 hover:text-[#85d7f9] transition-colors break-all">
                  {contactInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-xs text-gray-400">
            © {currentYear} {contactInfo.name}. {t({ da: "Alle rettigheder forbeholdes.", en: "All rights reserved." })}
          </p>
          <div className="flex space-x-6">
            <span className="text-xs text-gray-400">{t({ da: "CVR: På forespørgsel", en: "CVR: Upon request" })}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
